/* eslint-disable import/no-unresolved */
import BaseElement from 'base-element'
import { css, html, ref, createRef } from 'lit-all'
import styles from 'lib-styles'
import * as paths from 'lib-paths'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
/* eslint-disable import/no-unresolved */

class HHProductGallery extends BaseElement {
  static properties = {
    images: {
      type: Array,
    },
    zoomed: {
      type: Boolean,
      reflect: true,
    },
    activeIndex: {
      type: Number,
    },
    _setInstant: {
      type: Boolean,
      state: true,
    },
  }

  THUMBS_PER_PAGE = 3

  $carousel = createRef()
  $thumbnails = createRef()
  $thumbnails = []

  constructor() {
    super()
    this.images = []
    this.activeIndex = 0
    this._setCarouselSlide = false
    this._setInstant = false
  }

  updated(changedProperties) {
    if (changedProperties.has('images')) {
      if (!this.images.length) return
      this.$carousel.value.addEventListener(
        'sl-slide-change',
        this._handleCarouselSlideChange
      )
    }
    if (changedProperties.has('activeIndex')) {
      const scrollBehavior = this._setInstant ? 'instant' : 'smooth'
      if (this._setCarouselSlide) {
        this.$carousel.value.goToSlide(this.activeIndex, scrollBehavior)
      }
      this.$thumbnails.value.goToSlide(this.activeIndex, scrollBehavior)
      this._setCarouselSlide = false
    }
  }

  _handleCarouselSlideChange = (event) => {
    this.activeIndex = event.detail.index
  }

  setActiveSlide(index) {
    this._setCarouselSlide = true
    this.activeIndex = index
  }

  async setActiveSlideInstant(index) {
    this._setInstant = true
    this._setCarouselSlide = true
    this.activeIndex = index
    await this.updateComplete
    this._setInstant = false
  }

  _handleThumbCarouselPreviousClick = () => {
    if (this.activeIndex === 0) return
    this.setActiveSlide(this.activeIndex - 1)
  }

  _handleThumbCarouselNextClick = () => {
    if (this.activeIndex === this.images.length - 1) return
    this.setActiveSlide(this.activeIndex + 1)
  }

  _handleImageClick = () => {
    this.zoomed = true
    disableBodyScroll(this)
  }

  _handleZoomClick = () => {
    // this.zoomed = true
  }

  _handleCloseClick = () => {
    this.zoomed = false
    clearAllBodyScrollLocks()
  }

  render() {
    return html`
      <div class="thumbnails__container">
        <button
          class="navigation-button navigation-button--previous${this
            .activeIndex === 0
            ? ' hidden'
            : ''}"
          @click=${this._handleThumbCarouselPreviousClick}
          aria-label="Previous Slide"
        >
          ${paths.caret}
        </button>
        <sl-carousel
          class="thumbnails"
          ${ref(this.$thumbnails)}
          slides-per-page=${this.THUMBS_PER_PAGE}
          slides-per-move="1"
        >
          ${this.images?.map(
            (image, index) => html`
              <sl-carousel-item
                class=${this.activeIndex === index ? 'active' : ''}
              >
                <img
                  width="45"
                  height="45"
                  class="thumbails__image"
                  src=${image.preview_image}
                  alt="${image.alt} - media thumbnail"
                  @click=${() => this.setActiveSlide(index)}
                />
              </sl-carousel-item>
            `
          )}
        </sl-carousel>
        <button
          class="navigation-button navigation-button--next${this.activeIndex ===
          this.images.length - 1
            ? ' hidden'
            : ''}"
          @click=${this._handleThumbCarouselNextClick}
          aria-label="Next Slide"
        >
          ${paths.caret}
        </button>
      </div>
      <sl-carousel
        class="carousel"
        ${ref(this.$carousel)}
        navigation
        pagination
      >
        <div slot="previous-icon" ?hidden=${this.activeIndex === 0}>
          ${paths.navigationArrow}
        </div>
        <div
          slot="next-icon"
          ?hidden=${this.activeIndex === this.images.length - 1}
        >
          ${paths.navigationArrow}
        </div>
        ${this.images?.map(
          (image) => html`
            <sl-carousel-item>
              <img
                src=${image.src}
                alt=${image.alt}
                @click=${this._handleImageClick}
              />
            </sl-carousel-item>
          `
        )}
      </sl-carousel>
      <div id="zoomed-actions">
        <!-- TODO: Zoom/panning disabled pending full implementation -->
        <!-- <button
          aria-label="Zoom"
          class="zoomed-action-button zoomed-action-button--zoom"
          @click=${this._handleZoomClick}
        >
          ${paths.zoom}
        </button> -->
        <button
          aria-label="Close"
          class="zoomed-action-button zoomed-action-button--close"
          @click=${this._handleCloseClick}
        >
          ${paths.close}
        </button>
      </div>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.typography,
    styles.skeleton,
    styles.color,
    styles.animation,
    styles.button,
    css`
      :host {
        display: block;
        transition: all 250ms ease;
        position: sticky;
        top: 125px;
      }
      :host([zoomed]) {
        background-color: white;
        position: fixed;
        top: 0;
        left: 0;
        height: 100vh;
        z-index: 9;
      }
      #zoomed-actions {
        position: fixed;
        top: 0;
        right: 0;
        display: none;
      }
      :host([zoomed]) #zoomed-actions {
        display: flex;
      }
      .zoomed-action-button {
        background-color: rgba(24, 24, 27, 0.65);
        color: white;
        cursor: pointer;
        width: 46px;
        height: 46px;
        transition: background-color 0.15s ease;
      }
      .zoomed-action-button:hover {
        background-color: rgba(70, 70, 73, 0.65);
      }
      .zoomed-action-button svg {
        width: 24px;
        height: 24px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.5;
        stroke-linecap: round;
        stroke-linejoin: round;
        transition: opacity 0.15s ease;
        transform: none;
        filter: drop-shadow(1px 1px 1px rgba(24, 24, 27, 0.15));
        pointer-events: none;
      }
      .zoomed-action-button--close svg {
        width: 18px;
        height: 18px;
        stroke-width: 0.5;
      }
      .carousel {
        --aspect-ratio: 1;
        --slide-gap: 0px;
        max-height: 100%;
      }
      sl-carousel-item img {
        cursor: var(--zoom-icon-url), auto;
        object-fit: cover;
      }
      :host([zoomed]) sl-carousel-item img {
        /* cursor: zoom-in; */
        object-fit: contain;
      }
      .carousel::part(base) {
        gap: 0;
      }
      .carousel::part(scroll-container) {
        margin-bottom: 15px;
      }
      .carousel::part(navigation-button) {
        position: absolute;
        display: none;
      }
      .carousel::part(navigation-button--previous) {
        left: 18px;
        transform: rotate(180deg);
      }
      .carousel::part(navigation-button--next) {
        right: 18px;
      }
      .carousel::part(pagination-item) {
        height: 8px;
        width: 8px;
      }
      .carousel::part(pagination-item--active) {
        transform: unset;
      }
      .thumbnails__container {
        position: absolute;
        top: 20px;
        left: 20px;
        z-index: 100;
        width: 250px;
        z-index: 1;
        display: none;
      }
      .thumbnails {
        --scroll-hint: 10%;
        --aspect-ratio: 1;
        --slide-gap: 5px;
      }
      .thumbnails::part(base) {
        gap: 0;
      }
      .thumbnails sl-carousel-item {
        background-color: #f5f5f5;
        border-radius: 3px;
        border: 1px solid transparent;
        min-width: 45px;
        min-height: 45px;
      }
      .thumbnails sl-carousel-item.active {
        border: 1px solid #35332b;
      }
      .thumbnails__container .navigation-button {
        width: 30px;
        height: 18px;
        align-self: center;
        background-color: transparent;
        cursor: pointer;
        transition: opacity 0.15s ease;
      }
      .thumbnails__container .navigation-button.hidden {
        visibility: visible;
        pointer-events: none;
      }
      .thumbnails__container .navigation-button--previous {
        transform: rotate(90deg);
      }
      .thumbnails__container .navigation-button--next {
        transform: rotate(-90deg);
      }
      .thumbails__image {
        cursor: pointer;
        object-fit: cover;
      }
      @media screen and (min-width: 768px) {
        .thumbnails__container {
          display: flex;
        }
        .carousel::part(navigation-button) {
          display: block;
        }
        .carousel::part(pagination) {
          display: none;
        }
      }
    `,
  ]
}

customElements.define('hh-product-gallery', HHProductGallery)
